import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {ILocation} from "../../interfaces/ILocation";
import {AdminControllerService} from "../../../core/api/controllers/admin/admin-controller.service";
import {IOrganizationsWithLocationsReq} from "../../../core/api/controllers/admin/data/IOrganizationsWithLocationsReq";
import {IOrganization} from "../../interfaces/IOrganization";
import {PageEvent} from "@angular/material/paginator";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {IOrganizationsWithLocationsRes} from "../../../core/api/controllers/admin/data/IOrganizationsWithLocationsRes";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: 'app-organizations-with-locations-form',
  templateUrl: './organizations-with-locations-form.component.html',
  styleUrls: ['./organizations-with-locations-form.component.scss'],
  host: {
    class: "h-full"
  }
})
export class OrganizationsWithLocationsFormComponent implements OnInit{
  page = 0;
  itemsPerPage = 10;
  search = "";
  /** The term the currently displayed results were fetched with, used for the empty state and username hints. */
  activeSearch = "";
  totalCount = 0;
  pageSizeOptions = [5, 10, 25, 50];
  organizations: IOrganization[] = [];
  searchedOrganizations: IOrganization[] = [];
  selectedLocations: ILocation[] = [];
  @Output() _locationPressed = new EventEmitter<ILocation>();
  @Input() showCheckboxes = true;
  @Input() disabledLocationIds: string[] = [];
  @Input() requireActive = false;
  @Input() confirmationPrefix = "Apply this action";
  @Input() submitLabel = "Submit";
  @Input() searchPlaceholder = "Search organizations, locations or users";
  /**
   * Hides the built-in Cancel/Submit row so the picker can be embedded as a plain selection
   * control inside a larger form. Hosts that do this track selection via (_locationPressed)
   * and supply their own submit action. Defaults to true for the existing dialog usages.
   */
  @Input() showActions = true;

  private _searchSubject = new Subject<string>();
  $searchObservable = this._searchSubject.asObservable();

  @Output() _submit = new EventEmitter<string[]>();
  @Output() _cancel = new EventEmitter<boolean>();
  isLoading = false;


  constructor(
      private adminController: AdminControllerService,
      private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subscribeToSearch();
    this.getOrganizationsWithLocations();
  }

  pressLocation(location: ILocation): void {
    if (this.isLocationDisabled(location)) return;
    this._locationPressed.next(location);
    if(location.isSelected) this.selectedLocations.push(location);
    else this.selectedLocations = this.selectedLocations.filter(loc => loc.id !== location.id);

  }

  onSubmit(): void {
    if (this.selectedLocations.length === 0) return;
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Confirm destination locations",
        message: `${this.confirmationPrefix} to ${this.selectedLocations.length} selected active location${this.selectedLocations.length === 1 ? "" : "s"}?`,
        confirmButtonLabel: "Start push",
      },
    }).afterClosed().subscribe({
      next: (res) => {
        if(res) this.emitSubmit();
      }
    })
  }

  isLocationDisabled(location: ILocation): boolean {
    return this.disabledLocationIds.includes(location.id) ||
      (this.requireActive && location.isActive === false);
  }

  onCancel(): void {
    this._cancel.emit(true);

  }

  emitSubmit(): void {
    this._submit.emit(this.selectedLocations.map(location => location.id));
  }

  changeSearch(): void {
    this._searchSubject.next(this.search);
  }

  clearSearch(): void {
    if (!this.search) return;
    this.search = "";
    this.page = 0;
    this.changeSearch();
  }

  /**
   * Drops every selection and notifies the host for each location it had reported as selected,
   * so embedders that mirror the selection stay in sync.
   */
  clearSelection(): void {
    const previouslySelected = this.selectedLocations;
    this.selectedLocations = [];
    this.deselectAllIn(this.organizations);
    this.deselectAllIn(this.searchedOrganizations);
    previouslySelected.forEach(location => {
      location.isSelected = false;
      this._locationPressed.next(location);
    });
  }

  trackByOrganizationId(_index: number, organization: IOrganization): string {
    return organization.id;
  }

  subscribeToSearch(): void {
    this.$searchObservable.pipe(
        debounceTime(250),
        distinctUntilChanged()).subscribe(res => {
          this.search = res;
          this.getOrganizationsWithLocations();
    })
  }


  changePage(event: PageEvent): void {
    this.page = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.getOrganizationsWithLocations();
  }

  getOrganizationsWithLocations(): void {
    this.isLoading = true;
    const payload = this.createPayload();
    this.adminController.getOrganizationsWithLocations(payload).subscribe({
      next: (res) => {
        res = this.mapSelectedLocations(res);
        this.totalCount = res.totalCount;
        this.organizations = res.data;
        this.activeSearch = payload.search ?? "";
        this.searchedOrganizations.push(...this.organizations);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    })
  }


  mapSelectedLocations(res: IOrganizationsWithLocationsRes): IOrganizationsWithLocationsRes {
   res.data.map(organization => this.mapSelectedOrganzation(organization));
    return res;
  }

  mapSelectedOrganzation(organization: IOrganization): IOrganization {
    const fetchedOrganization = this.searchedOrganizations.find(org => {
      return org.id === organization.id;
    })
    if(!fetchedOrganization) return organization;

    organization.locations.forEach(location => {
      location.isSelected = this.selectedLocations.some(loc => loc.id === location.id);
    })

    return organization;
  }

  createPayload(): IOrganizationsWithLocationsReq {
    return {
      page: this.page,
      itemsPerPage: this.itemsPerPage,
      search: this.search?.trim() ?? null
    }
  }

  private deselectAllIn(organizations: IOrganization[]): void {
    organizations.forEach(organization =>
        organization.locations?.forEach(location => location.isSelected = false));
  }

}
